"use client";

/**
 * BodyWithFooter component
 * Wraps the main body and global footer, ensuring footer is only shown after body is loaded.
 */

import React, { Suspense, useEffect, useState } from "react";
import Body from "./Body";
const GlobalFooter = dynamic(() => import("./footer/GlobalFooter"),{ssr: false});
import { IOffer } from "./header/OfferBanner";
import { sendGTMEvent } from '@next/third-parties/google';
import { HeaderType } from "@/utils/desingTemplateUtils";
import { gtpIdToHash } from "@/lib/utils";
import dynamic from "next/dynamic";

interface Auth0User {
  email?: string;
  email_verified?: boolean;
  name?: string;
  nickname?: string;
  picture?: string;
  sub?: string;
  updated_at?: string;
  CapResponse?: {
    loyalty_ID?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export default function BodyWithFooter({
  children,
  offer,
  emergencyActive,
  isShowGotoPass,
  footerProps,
  pathname,
  enableAuth,
  header_name
}: Readonly<{
  children: React.ReactNode;
  offer: IOffer;
  emergencyActive: boolean;
  isShowGotoPass: boolean;
  footerProps: React.ComponentProps<typeof GlobalFooter>;
  pathname: string;
  enableAuth?: boolean;
  header_name?: HeaderType
}>) {
  /**
   * Track if body is loaded to conditionally render footer
   */
  const [isBodyLoaded, setIsBodyLoaded] = useState(false);
  const [session, setSession] = useState('');
  const [user, setUser] = useState<any>(null);

  
  useEffect(() => {
    const fetchSession = async () => {
      try {
        const sessionRes = await fetch('/api/getSession');
        if (!sessionRes.ok) {
          throw new Error('Failed to fetch session');
        }
        const sessionData = await sessionRes.json();
        setSession(sessionData?.session)
        setUser(sessionData?.user)
      } catch (error) {
        console.error('Error fetching session:', error);
      }
    };

    fetchSession();
  }, []);

  useEffect(() => {
    if (!user || Object.keys(user).length === 0) return;
    const localStorageKey = 'iconia_website_firstvisit';
    const currentLoyaltyId = user?.CapResponse?.loyalty_ID;

    let storedData: Record<string, string>;
    try {
      const parsed = JSON.parse(localStorage.getItem(localStorageKey) ?? '{}');
      storedData = typeof parsed === 'object' && parsed !== null ? parsed : {};
    } catch {
      storedData = {};
    }

    if (user?.login_count === 1 && !storedData[currentLoyaltyId]) {
      sendGTMEvent({
        event: 'first_iconia_website_visit',
        membership_id: gtpIdToHash(user?.CapResponse?.loyalty_ID),
      });
      storedData[currentLoyaltyId] = 'true';
      localStorage.setItem(localStorageKey, JSON.stringify(storedData));
      return;
    }

    if (user?.login_count > 1 && !storedData[currentLoyaltyId]) {
      sendGTMEvent({
        event: 'iconia_website_login',
        membership_id: gtpIdToHash(user?.CapResponse?.loyalty_ID),
      });
      storedData[currentLoyaltyId] = 'true';
      localStorage.setItem(localStorageKey, JSON.stringify(storedData));
      return;
    }
    if (user?.login_count > 1) {
      sendGTMEvent({
        event: 'iconia_website_login',
        membership_id: gtpIdToHash(user?.CapResponse?.loyalty_ID),
      });
      return;
    }
  }, [user]);

  return (
    <>
      <Body
        pathname={pathname}
        offer={offer}
        emergencyActive={emergencyActive}
        isShowGotoPass={isShowGotoPass}
        onLoaded={() => setIsBodyLoaded(true)}
        addMarginClasses={header_name !== "wan-wan" && header_name !== "art" && header_name !== "msp"}
      >
        {children}
      </Body>
      {isBodyLoaded && <Suspense><GlobalFooter {...footerProps} session={session} enableAuth={enableAuth} /></Suspense>}
    </>
  );
}